/* FFC Personalisation Configurator — DTF / Embroidery logo personalisation.
   Vanilla JS, no framework. Self-initialises on [data-ffc-personalisation]
   and exposes a small public bridge (window.FFCPersonalisation) so the
   existing product configurator (ffc-product-configurator.js) can open the
   panel and receive the finished line-item properties, without either file
   needing to know the other's internals.

   Preview garment is a real WebGL 3D model, driven through a second bridge
   (window.FFCPersonalisation3D, see ffc-personalisation-3d.js) -- this file
   owns method/pricing/placement/validation state and just tells that
   bridge which placements are chosen, what the current artwork/colour/
   rotation are, and lets it render. This replaced an earlier inline-SVG
   illustration; PLACEMENTS below still carries each placement's `side`
   (front/back, used to decide which way to spin the model when a
   placement is added/selected) and `widthCm` (used only for the tile price
   label's "~10.5 cm" text) -- the actual on-model position/size/rotation
   calibration now lives in ffc-personalisation-3d.js's own ZONES table,
   keyed by the same placement ids. */
(function () {
  'use strict';

  var GBP = String.fromCharCode(163);
  function money(pence) {
    var n = (Number(pence) || 0) / 100;
    return GBP + n.toFixed(2);
  }

  var MAX_FILE_MB = 15;
  var ACCEPTED_MIME = {
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/svg+xml': 'SVG',
    'application/pdf': 'PDF'
  };
  var ACCEPTED_EXT = /\.(png|jpe?g|svg|pdf)$/i;

  // `x`/`y` below are legacy 2D-stage percentages, no longer read by the
  // renderer (kept only because removing them means touching every entry
  // for zero behavioural change) -- real placement calibration is now
  // ffc-personalisation-3d.js's ZONES table. Still authoritative here:
  // `side` (front/back) plus `viewDeg` (which way to spin the model on
  // add/select -- see viewDegFor below) and `widthCm` (used only for the
  // tile price label's "~10.5 cm" text). `methods` restricts which
  // personalisation method may use that placement (back = DTF only).
  // One chest DTF placement can be included for pack garments; every other
  // DTF spot and all embroidery use the automatic per-placement price. WEARER's
  // perspective still applies to
  // the ids themselves: "left chest/sleeve" is the wearer's own left
  // side, which appears on the *right* half of a front-facing view --
  // ffc-personalisation-3d.js's ZONES table must stay consistent with this
  // if it's ever re-calibrated.
  var PLACEMENTS = [
    { id: 'left_chest', label: 'Left chest', x: 56.4, y: 41, side: 'front', viewDeg: 0, icon: '👕', widthCm: 11, methods: ['dtf', 'embroidery'] },
    { id: 'right_chest', label: 'Right chest', x: 43.6, y: 41, side: 'front', viewDeg: 0, icon: '👕', widthCm: 11, methods: ['dtf', 'embroidery'] },
    { id: 'left_sleeve', label: 'Left sleeve', x: 78.3, y: 32, side: 'front', viewDeg: -90, icon: '💪', widthCm: 7.5, methods: ['dtf', 'embroidery'] },
    { id: 'right_sleeve', label: 'Right sleeve', x: 21.7, y: 32, side: 'front', viewDeg: 90, icon: '💪', widthCm: 7.5, methods: ['dtf', 'embroidery'] },
    { id: 'neck', label: 'Nape (neck label)', x: 50, y: 13, side: 'back', viewDeg: 180, icon: '🏷️', widthCm: 8, methods: ['dtf', 'embroidery'] },
    { id: 'back', label: 'Back', x: 50, y: 53, side: 'back', viewDeg: 180, icon: '🔙', widthCm: 21, methods: ['dtf'] }
  ];

  // Which way to spin the model when a placement is added or selected.
  // `side` (front/back) alone can't express this: a sleeve is neither, and
  // treating it as "front" left the shopper looking at the chest while the
  // logo sat on the far edge of an arm pointing away -- reported as "the
  // graphic doesn't land on the sleeve". The sleeve has to face the camera,
  // which happens at a quarter turn.
  // Handedness CONFIRMED ON SCREEN, not from pixel statistics: an earlier
  // pass read the centroid of a decal applied outside the normal flow and
  // concluded +90 for left_sleeve, which was wrong -- the owner saw the
  // opposite sleeve. Looking at the render with a real uploaded logo, the
  // LEFT sleeve is the one facing the camera at -90, right_sleeve at +90.
  // If the GLB is ever swapped, re-check by eye rather than trusting a
  // pixel count.
  function viewDegFor(placement) {
    if (!placement) return 0;
    if (typeof placement.viewDeg === 'number') return placement.viewDeg;
    return placement.side === 'back' ? 180 : 0;
  }

  // Authoritative pricing defaults (pence). Overridable per-section via the
  // JSON block rendered by ffc-personalisation-panel.liquid, which itself
  // reads section settings -- so there is one editable source (theme
  // editor), not values duplicated across files. Embroidery is charged
  // PER PLACEMENT (not once for the whole order) -- each embroidered spot
  // is separate machine time -- and splits into Logo (customer's uploaded
  // artwork, needs DST digitisation if not supplied) and Text (stock
  // lettering, no digitisation ever needed, cheaper).
  //
  // Per-placement model (gross customer prices): DTF is charged per garment
  // and selected spot, embroidery is charged per garment and selected spot,
  // and DST preparation is a one-off project fee. A pack can include exactly
  // one chest DTF placement for its own garments; ordinary blank items never
  // inherit that inclusion just because they share the same cart.
  var FREE_DTF_IDS = ['left_chest', 'right_chest'];

  var DEFAULT_PRICING = {
    embroideryPlacement: 800,
    dtfPlacement: 250,
    includedDtfQty: 0,
    // Prog minimalny liczony w SZTUKACH objetych tym projektem. Ponizej
    // progu personalizacja jest niedostepna, wiec i wliczone miejsce na
    // piersi nie przysluguje. Wartosc realna przychodzi z ustawien motywu
    // (settings.ffc_perso_min_qty) przez panel; 4 to tylko awaryjny domysl.
    minQty: 4,
    dst: 2000
  };

  // Common apparel colour names -> hex, for recolouring the SVG garment to
  // roughly match the variant the shopper picked. Best-effort: covers the
  // supplier colour names actually seen in this catalogue; anything
  // unmatched falls back to a neutral mid-grey rather than guessing wrong.
  var COLOUR_MAP = [
    [/black/i, '#1c1c1c'], [/white/i, '#f5f5f0'], [/natural/i, '#e8ddc7'],
    [/navy/i, '#1b2a4a'], [/royal/i, '#1c4fa0'], [/sky\s*blue/i, '#7ec8e3'],
    [/light\s*blue/i, '#a9d3e5'], [/turquoise/i, '#1f9e9b'], [/teal/i, '#137a6f'],
    [/sport\s*grey|sports\s*grey/i, '#a8a9ad'], [/charcoal/i, '#4b4b4d'],
    [/dark\s*heather/i, '#565656'], [/heather\s*grey|heather\s*gray/i, '#b7b8ba'],
    [/grey|gray/i, '#8f9192'], [/cherry\s*red/i, '#a51931'], [/red/i, '#c8102e'],
    [/maroon|burgundy/i, '#6c1f2e'], [/bottle\s*green/i, '#1c3a2e'],
    [/kelly\s*green/i, '#2e8b3d'], [/green/i, '#2e7d32'], [/purple/i, '#5b3a8e'],
    [/orange/i, '#d9622b'], [/gold/i, '#d4af37'], [/yellow/i, '#f2c230'],
    [/light\s*pink/i, '#f2c2d4'], [/pink/i, '#e8a0bf'], [/beige/i, '#d8c9a3'],
    [/khaki/i, '#8a7b5c'], [/stone/i, '#c9c0ab'], [/chocolate|brown/i, '#4a3222']
  ];
  var DEFAULT_SHIRT_COLOUR = '#9a978c';
  // Fallback swatches, used only to top the preview row up when the cart
  // lines being personalised don't span enough colours by themselves. Names
  // must be ones COLOUR_MAP above can resolve, and are the supplier's own
  // English colour names so they read the same as the product options.
  var MAIN_PREVIEW_COLOURS = ['White', 'Sport Grey', 'Black', 'Navy', 'Red', 'Bottle Green'];
  var MIN_PREVIEW_SWATCHES = 5;
  // Keys only -- the actual backdrops are layered gradients in
  // ffc-personalisation.css (studio falloff + a contact shadow under the
  // garment), which a single hex can't express. JS just stamps the key.
  var STAGE_BACKGROUNDS = { white: 1, grey: 1, black: 1,
    lounge: 1, industrial: 1, street: 1, salon: 1 };
  function hexForColourName(name) {
    if (!name) return DEFAULT_SHIRT_COLOUR;
    for (var i = 0; i < COLOUR_MAP.length; i++) if (COLOUR_MAP[i][0].test(name)) return COLOUR_MAP[i][1];
    return DEFAULT_SHIRT_COLOUR;
  }

  function byId(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function dstUiState(placements, activePlacementId, dstStatus) {
    var hasEmbLogo = placements.some(function (chosen) {
      return chosen.method === 'embroidery' && chosen.embMode === 'logo';
    });
    var activePlacement = activePlacementId ? byId(placements, activePlacementId) : null;
    var activeNeedsDst = !!activePlacement && activePlacement.method === 'embroidery' && activePlacement.embMode === 'logo';
    var savedWhileAddingNonDst = hasEmbLogo && !!dstStatus && !!activePlacementId && !activeNeedsDst;
    return {
      hasEmbLogo: hasEmbLogo,
      disabled: !hasEmbLogo || savedWhileAddingNonDst,
      savedWhileAddingNonDst: savedWhileAddingNonDst
    };
  }

  // ---------------------------------------------------------------------
  // Upload
  // ---------------------------------------------------------------------
  var Upload = {
    validate: function (file) {
      if (!file) return 'No file selected.';
      var typeOk = ACCEPTED_MIME[file.type] || ACCEPTED_EXT.test(file.name || '');
      if (!typeOk) return 'Unsupported file format. Allowed: PNG, JPG, SVG or PDF.';
      var maxBytes = MAX_FILE_MB * 1024 * 1024;
      if (file.size > maxBytes) return 'The file is too large (max ' + MAX_FILE_MB + ' MB).';
      return null;
    },
    typeLabel: function (file) {
      return ACCEPTED_MIME[file.type] || (ACCEPTED_EXT.exec(file.name || '') || [, ''])[1].toUpperCase();
    },
    sizeLabel: function (bytes) {
      if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      return Math.round(bytes / 1024) + ' KB';
    },
    // Safe preview: always via <img src="blob:...">, never innerHTML/inline
    // <svg>/<object> -- an <img> never executes script content even for
    // SVG sources, which a directly-injected <svg> or <object> element can.
    canPreviewVisually: function (file) {
      return file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/svg+xml';
    },
    // Natural pixel aspect ratio, used to derive each placement's overlay
    // height from its fixed real-world width (section: "wysokość
    // proporcjonalnie do szerokości"). Resolves to null for non-raster
    // previews (PDF) where there is nothing to measure.
    naturalAspect: function (url) {
      return new Promise(function (resolve) {
        if (!url) { resolve(null); return; }
        var img = new Image();
        img.onload = function () { resolve(img.naturalWidth && img.naturalHeight ? img.naturalHeight / img.naturalWidth : null); };
        img.onerror = function () { resolve(null); };
        img.src = url;
      });
    }
  };

  // ---------------------------------------------------------------------
  // Pricing
  // ---------------------------------------------------------------------
  // Which DTF spot (if any) gets the single included slot: the first CHEST
  // the shopper added with DTF. Returns an index into state.placements so
  // the caller can tell "this exact one is free" from "a chest is free but
  // it isn't this one" -- picking by id alone would wrongly free both chests
  // when the shopper adds left AND right.
  // Wliczone miejsce przysluguje tylko rzeczywistym komponentom pakietu.
  // Samo przekroczenie minimum personalizacji nigdy nie uruchamia tej ulgi;
  // przy zwyklym zamowieniu kazde miejsce DTF jest platne.
  function freeDtfIndex(placements, cfg) {
    if (cfg && cfg.freeDtf === false) return -1;
    for (var i = 0; i < placements.length; i++) {
      var c = placements[i];
      if (c.method !== 'embroidery' && FREE_DTF_IDS.indexOf(c.id) > -1) return i;
    }
    return -1;
  }

  var Pricing = {
    compute: function (state, cfg, qty) {
      qty = Math.max(1, qty || 1);
      var lines = [];
      var freeIdx = freeDtfIndex(state.placements, cfg);
      var includedQty = Math.min(qty, Math.max(0, Number(cfg.includedDtfQty) || 0));
      var dtfServiceQty = 0;
      var embroideryServiceQty = 0;

      state.placements.forEach(function (chosen, idx) {
        var placement = byId(PLACEMENTS, chosen.id);
        if (!placement) return;

        if (chosen.method === 'embroidery') {
          var embroideryLabel = chosen.embMode === 'text'
            ? 'Embroidery — text (' + placement.label + (chosen.text ? ': "' + chosen.text + '"' : '') + ')'
            : 'Embroidery — logo (' + placement.label + ')';
          embroideryServiceQty += qty;
          lines.push({ key: 'method:' + placement.id, label: embroideryLabel, perGarment: cfg.embroideryPlacement, perGarmentQty: qty });
        } else if (idx === freeIdx) {
          var paidQty = Math.max(0, qty - includedQty);
          dtfServiceQty += paidQty;
          lines.push({
            key: 'method:' + placement.id,
            label: 'DTF (' + placement.label + ')' + (paidQty > 0 && includedQty > 0 ? ' — ' + includedQty + ' pack garments included' : ''),
            perGarment: cfg.dtfPlacement,
            perGarmentQty: paidQty,
            included: paidQty === 0,
            includedQty: includedQty
          });
        } else {
          dtfServiceQty += qty;
          lines.push({ key: 'method:' + placement.id, label: 'DTF (' + placement.label + ')', perGarment: cfg.dtfPlacement, perGarmentQty: qty });
        }
      });

      var hasEmbLogo = state.placements.some(function (chosen) {
        return chosen.method === 'embroidery' && chosen.embMode === 'logo';
      });
      var dstServiceQty = hasEmbLogo && state.dstStatus !== 'yes' ? 1 : 0;
      if (dstServiceQty) {
        lines.push({ key: 'dst-setup', label: 'DST file preparation', flat: cfg.dst });
      }

      var total = 0;
      lines.forEach(function (l) {
        if (l.perGarment) total += l.perGarment * (l.perGarmentQty || 0);
        if (l.flat) total += l.flat;
      });
      return {
        lines: lines,
        total: total,
        manualQuote: false,
        freeDtfIndex: freeIdx,
        includedDtfQty: includedQty,
        serviceQuantities: {
          dtf: dtfServiceQty,
          embroidery: embroideryServiceQty,
          dst: dstServiceQty
        }
      };
    }
  };

  // ---------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------
  var MESSAGES = {
    placement: 'Please choose at least one logo placement.',
    artwork: 'Please upload your logo.',
    rights: 'Please confirm you have the right to use this design.',
    text: 'Please enter the text to embroider for every placement set to "Text".',
    dst: 'Please answer the DST file question.',
    uploadPending: 'Your logo is still being saved — one moment please.',
    uploadFailed: 'Your logo could not be saved. Please try uploading it again.'
  };
  // Method/artwork/DST requirements now depend on what's actually chosen
  // per placement (mixed DTF + Embroidery, Logo + Text, is normal), not on
  // one global method -- so validation scans all placements rather than
  // checking a single state.method.
  var Validation = {
    check: function (state) {
      var missing = [];
      if (!state.placements.length) { missing.push('placement'); return missing; }
      var needsArtwork = state.placements.some(function (c) { return c.method === 'dtf' || (c.method === 'embroidery' && c.embMode === 'logo'); });
      var needsText = state.placements.some(function (c) { return c.method === 'embroidery' && c.embMode === 'text' && !c.text.trim(); });
      var needsDst = state.placements.some(function (c) { return c.method === 'embroidery' && c.embMode === 'logo'; });
      if (needsArtwork && !state.artwork) missing.push('artwork');
      if (needsArtwork && !state.rightsConfirmed) missing.push('rights');
      // Only blocks confirm when an upload endpoint is actually configured
      // (Controller.prototype.updateConfirmState / confirm pass
      // `hasUploadEndpoint` in) -- with no endpoint set, the artwork stays
      // browser-only exactly like before this feature existed, and never
      // blocks anything.
      if (needsArtwork && state.artwork && state.artwork.uploadEndpointConfigured) {
        if (state.artwork.uploadStatus === 'pending') missing.push('uploadPending');
        else if (state.artwork.uploadStatus === 'error') missing.push('uploadFailed');
      }
      if (needsText) missing.push('text');
      if (needsArtwork && state.quality && state.quality.editingPending) missing.push('artworkEditing');
      if (state.qualityRequired && state.placements.some(function (c) { return c.method === 'dtf'; })) {
        if (!state.quality || !state.quality.ready) missing.push('artworkQuality');
      }
      if (needsDst && !state.dstStatus) missing.push('dst');
      return missing;
    },
    messageFor: function (key) { return MESSAGES[key] || ''; }
  };

  // ---------------------------------------------------------------------
  // Controller (one per panel instance; realistically one per product page)
  // ---------------------------------------------------------------------
  function Controller(root) {
    this.root = root;
    this.cfg = DEFAULT_PRICING;
    this.uploadEndpointUrl = ''; // set via theme editor; blank = artwork stays browser-only (old behaviour)
    try {
      var dataEl = root.querySelector('[data-ffc-personalisation-data]');
      if (dataEl) {
        var parsed = JSON.parse(dataEl.textContent);
        if (parsed && parsed.pricing) {
          this.cfg = {
            embroideryPlacement: Number(parsed.pricing.embroideryPlacement) || DEFAULT_PRICING.embroideryPlacement,
            dtfPlacement: Number(parsed.pricing.dtfPlacement) || DEFAULT_PRICING.dtfPlacement,
            dst: Number(parsed.pricing.dst) || DEFAULT_PRICING.dst,
            minQty: Number(parsed.minQty) || DEFAULT_PRICING.minQty,
            freeDtf: parsed.freeDtf === true,
            includedDtfQty: 0
          };
        }
        if (parsed && typeof parsed.uploadEndpointUrl === 'string') {
          this.uploadEndpointUrl = parsed.uploadEndpointUrl.trim();
        }
      }
    } catch (e) { /* fall back to defaults; panel still works */ }

    this.state = this.freshState();
    // Only one placement can be edited at a time. Once committed, its
    // method/details stay immutable until the shopper explicitly chooses Edit.
    this.activePlacementId = null;
    this.artworkUrl = null; // object URL, revoked on replace/close
    this.artworkAspect = null; // naturalHeight / naturalWidth of current artwork
    this.qty = 1;
    this.onConfirm = null;
    this.garmentColour = DEFAULT_SHIRT_COLOUR;
    this.rotation = 0; // degrees, -180..180, drives the 3D model's Y rotation
    this._stage3dState = 'loading'; // 'loading' | 'ready' | 'failed', set by open()

    this.cacheEls();
    this.renderPlacements();
    this.applyGarmentColour();
    this.wireEvents();
  }

  Controller.prototype.freshState = function () {
    return {
      artwork: null, // { file, name, type, size, previewUrl, canPreview, uploadEndpointConfigured, uploadStatus, remoteUrl } -- shared by every DTF / Embroidery+Logo placement
      qualityRequired: true,
      quality: null,
      rightsConfirmed: false,
      // Each chosen placement carries its OWN method -- DTF and Embroidery
      // can be mixed freely across placements in one order. `embMode`
      // only applies when method === 'embroidery' ('logo' | 'text');
      // `text` only applies when embMode === 'text'.
      placements: [], // [{ id, method: 'dtf'|'embroidery', embMode: null|'logo'|'text', text, saved }]
      dstStatus: null, // 'yes' | 'no' | 'unsure' -- shared, applies once if ANY placement is Embroidery+Logo
      notes: ''
    };
  };

  Controller.prototype.cacheEls = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    var qa = function (sel) { return Array.prototype.slice.call(r.querySelectorAll(sel)); };
    this.els = {
      overlay: r,
      panel: q('[data-fp-panel]'),
      closeBtns: qa('[data-fp-close]'),
      dropzone: q('[data-fp-dropzone]'),
      fileInput: q('[data-fp-file-input]'),
      fileBtn: q('[data-fp-file-btn]'),
      artworkCard: q('[data-fp-artwork]'),
      artworkThumb: q('[data-fp-artwork-thumb]'),
      artworkName: q('[data-fp-artwork-name]'),
      artworkSub: q('[data-fp-artwork-sub]'),
      artworkRemove: q('[data-fp-artwork-remove]'),
      artworkReplace: q('[data-fp-artwork-replace]'),
      artworkUploadStatus: q('[data-fp-artwork-upload-status]'),
      uploadError: q('[data-fp-upload-error]'),
      rightsCheckbox: q('[data-fp-rights]'),
      rightsGated: qa('[data-fp-rights-gated]'),
      placementsWrap: q('[data-fp-placements]'),
      chosenWrap: q('[data-fp-chosen-placements]'),
      dstSection: q('[data-fp-dst-section]'),
      dstOpts: qa('[data-fp-dst]'),
      dstNote: q('[data-fp-dst-note]'),
      notes: q('[data-fp-notes]'),
      stage: q('[data-fp-stage]'),
      garmentSwatches: q('[data-fp-garment-swatches]'),
      bgBtns: qa('[data-fp-bg]'),
      rotateSlider: q('[data-fp-rotate-slider]'),
      rotateNudgeBtns: qa('[data-fp-rotate-nudge]'),
      stageLoading: q('[data-fp-stage-loading]'),
      stageEmpty: q('[data-fp-stage-empty]'),
      stageError: q('[data-fp-stage-error]'),
      summaryList: q('[data-fp-summary-list]'),
      summaryTotal: q('[data-fp-summary-total]'),
      addAnotherBtn: q('[data-fp-add-another]'),
      confirmBtn: q('[data-fp-confirm]'),
      footError: q('[data-fp-foot-error]'),
      liveStatus: q('[data-fp-live-status]')
    };
  };

  // Tile label has to reflect the free-chest rule, not a blanket "Wliczone":
  // a chest reads as included only while the free slot is still unclaimed
  // (or is claimed by this very tile); everything else shows what it costs,
  // so the shopper sees the surcharge BEFORE adding the spot rather than in
  // the summary afterwards.
  Controller.prototype.priceLabelFor = function (placement) {
    var suffix = ' · ~' + placement.widthCm + ' cm';
    if (this.allowDtf === false) {
      return this.methodAllowed(placement, 'embroidery')
        ? 'Embroidery ' + money(this.cfg.embroideryPlacement) + ' per garment' + suffix
        : this.methodUnavailable;
    }
    if (this.cfg && this.cfg.freeDtf === false) {
      return 'DTF ' + money(this.cfg.dtfPlacement) + ' per garment' + suffix;
    }

    var freeIdx = freeDtfIndex(this.state.placements, this.cfg);
    var isChest = FREE_DTF_IDS.indexOf(placement.id) > -1;

    // Zanim klient wybierze stronę, ZADEN kafelek piersi nie moze pisac
    // "Included": wliczona jest dokladnie jedna strona, a dwie etykiety
    // "Wliczone" naraz obiecywaly dwa darmowe miejsca. Ktora strona jest
    // wliczona, mowi zdanie nad kafelkami (data-fp-chest-hint).
    if (freeIdx === -1) {
      if (isChest) return 'One chest DTF placement included with your pack' + suffix;
      return 'DTF ' + money(this.cfg.dtfPlacement) + ' per garment' + suffix;
    }

    if (this.state.placements[freeIdx].id === placement.id) return 'DTF included for pack garments' + suffix;
    return 'DTF ' + money(this.cfg.dtfPlacement) + ' per garment' + suffix;
  };

  // Zdanie "Choose one included chest position" ma sens tylko dopoki wybor
  // nie zostal dokonany — potem etykiety kafelkow mowia juz wszystko.
  Controller.prototype.refreshChestHint = function () {
    var hint = this.root.querySelector('[data-fp-chest-hint]');
    if (!hint) return;
    var claimed = freeDtfIndex(this.state.placements, this.cfg) > -1;
    var eligible = this.cfg && this.cfg.freeDtf !== false;
    hint.hidden = claimed || !eligible;
  };

  Controller.prototype.renderPlacements = function () {
    if (!this.els.placementsWrap) return;
    var self = this;
    this.els.placementsWrap.innerHTML = PLACEMENTS.map(function (p) {
      return (
        '<button type="button" class="ffc-personalisation__placement" data-fp-placement-card="' + p.id + '">' +
          '<span class="ffc-personalisation__placement-icon" aria-hidden="true">' + p.icon + '</span>' +
          '<span class="ffc-personalisation__placement-label">' + p.label + '</span>' +
          '<span class="ffc-personalisation__placement-price" data-fp-placement-price>' + self.priceLabelFor(p) + '</span>' +
        '</button>'
      );
    }).join('');
    this.els.placementCards = Array.prototype.slice.call(this.root.querySelectorAll('[data-fp-placement-card]'));
  };

  // A selected tile is represented by its chosen-placement card below and is
  // therefore disabled here. While one placement is being edited, the other
  // unselected tiles are also disabled: the shopper must explicitly save that
  // placement before starting another one.
  Controller.prototype.refreshPlacementAvailability = function () {
    var self = this;
    var chosenIds = this.state.placements.map(function (c) { return c.id; });
    var hasActivePlacement = !!this.activePlacementId;
    this.els.placementCards.forEach(function (card) {
      var id = card.getAttribute('data-fp-placement-card');
      var alreadyChosen = chosenIds.indexOf(id) > -1;
      var placement = byId(PLACEMENTS, id);
      var compatible = placement && placement.methods.some(function (method) { return self.methodAllowed(placement, method); });
      card.disabled = alreadyChosen || hasActivePlacement || !compatible;
      card.classList.toggle('is-chosen', alreadyChosen);
      card.title = alreadyChosen
        ? 'Selected — use Edit or Remove below'
        : (hasActivePlacement ? 'Save the current placement before adding another' : '');
      var priceEl = card.querySelector('[data-fp-placement-price]');
      if (placement && priceEl) priceEl.textContent = self.priceLabelFor(placement);
    });
    this.refreshChestHint();
    this.updateAddAnotherState();
  };

  Controller.prototype.methodAllowed = function (placement, method) {
    return !!placement && placement.methods.indexOf(method) !== -1 &&
      (method === 'dtf' ? this.allowDtf !== false : method === 'embroidery' && this.allowEmbroidery !== false);
  };

  Controller.prototype.methodsValid = function () {
    var self = this;
    return this.state.placements.every(function (chosen) { return self.methodAllowed(byId(PLACEMENTS, chosen.id), chosen.method); });
  };

  Controller.prototype.updateAddAnotherState = function () {
    if (!this.els.addAnotherBtn) return;
    var hasPlacement = this.state.placements.length > 0;
    var hasAvailablePlacement = this.state.placements.length < PLACEMENTS.length;
    this.els.addAnotherBtn.hidden = !hasPlacement || !hasAvailablePlacement;
    this.els.addAnotherBtn.disabled = !hasPlacement || !hasAvailablePlacement;
  };

  // A saved placement renders as a locked summary. Only the one active row
  // exposes method/detail controls, so a later DTF/Embroidery choice cannot
  // silently rewrite an earlier placement.
  Controller.prototype.renderChosenPlacements = function () {
    if (!this.els.chosenWrap) return;
    var self = this;
    var state = this.state;
    if (!state.placements.length) {
      this.els.chosenWrap.innerHTML = '';
      this.els.chosenWrap.hidden = true;
      this.updateDstVisibility();
      return;
    }
    this.els.chosenWrap.hidden = false;
    this.els.chosenWrap.innerHTML = state.placements.map(function (chosen) {
      var placement = byId(PLACEMENTS, chosen.id);
      if (!placement) return '';
      if (chosen.saved) {
        var savedDescription = chosen.method === 'embroidery'
          ? 'Embroidery — ' + (chosen.embMode === 'text' ? 'text: “' + chosen.text + '”' : 'logo')
          : 'DTF print';
        var editDisabled = self.activePlacementId && self.activePlacementId !== chosen.id;
        return (
          '<div class="ffc-personalisation__chip-row is-saved" data-fp-chip="' + chosen.id + '">' +
            '<div class="ffc-personalisation__chip-head">' +
              '<span class="ffc-personalisation__chip-label">' + placement.label + '</span>' +
              '<div class="ffc-personalisation__chip-saved-actions">' +
                '<span class="ffc-personalisation__chip-saved-badge">Saved</span>' +
                '<button type="button" class="ffc-personalisation__chip-edit" data-fp-chip-edit="' + chosen.id + '"' + (editDisabled ? ' disabled title="Save the current placement before editing this one"' : '') + '>Edit</button>' +
                '<button type="button" class="ffc-personalisation__chip-remove" data-fp-chip-remove="' + chosen.id + '" aria-label="Remove ' + placement.label + '">&times;</button>' +
              '</div>' +
            '</div>' +
            '<p class="ffc-personalisation__chip-saved-summary">' + escapeHtml(savedDescription) + '</p>' +
          '</div>'
        );
      }
      var allowedMethods = placement.methods.filter(function (m) {
        return self.methodAllowed(placement, m);
      });
      var methodBtns = allowedMethods.map(function (m) {
        var label = m === 'dtf'
          ? 'DTF (+' + money(self.cfg.dtfPlacement) + ' per garment)'
          : 'Embroidery (+' + money(self.cfg.embroideryPlacement) + ' per garment)';
        var isSel = chosen.method === m;
        return '<button type="button" class="ffc-personalisation__chip-method-btn' + (isSel ? ' is-selected' : '') + '" data-fp-chip-method="' + m + '" data-fp-chip-for="' + chosen.id + '">' + label + '</button>';
      }).join('');

      var embHtml = '';
      if (chosen.method === 'embroidery') {
        embHtml += '<div class="ffc-personalisation__chip-embtype">' + ['logo', 'text'].map(function (m) {
          var label = m === 'logo' ? 'Logo' : 'Text';
          var isSel = chosen.embMode === m;
          return '<button type="button" class="ffc-personalisation__chip-embtype-btn' + (isSel ? ' is-selected' : '') + '" data-fp-chip-embtype="' + m + '" data-fp-chip-for="' + chosen.id + '">' + label + '</button>';
        }).join('') + '</div>';
        if (chosen.embMode === 'text') {
          embHtml += '<input type="text" class="ffc-personalisation__chip-text" data-fp-chip-text data-fp-chip-for="' + chosen.id + '" maxlength="30" placeholder="' + escapeHtml('Enter the text to embroider') + '" value="' + escapeHtml(chosen.text) + '">';
        }
      }

      return (
        '<div class="ffc-personalisation__chip-row" data-fp-chip="' + chosen.id + '">' +
          '<div class="ffc-personalisation__chip-head">' +
            '<span class="ffc-personalisation__chip-label">' + placement.label + '</span>' +
            '<button type="button" class="ffc-personalisation__chip-remove" data-fp-chip-remove="' + chosen.id + '" aria-label="Remove ' + placement.label + '">&times;</button>' +
          '</div>' +
          '<div class="ffc-personalisation__chip-method">' + methodBtns + '</div>' +
          embHtml +
        '</div>'
      );
    }).join('');
    this.updateDstVisibility();
  };

  // DST question only applies to Embroidery+Logo spots (Text embroidery uses
  // stock lettering, DTF is printed -- neither needs digitisation). When it
  // doesn't apply the section stays ON SCREEN but goes inert: buttons
  // disabled, an explanation of why. It used to vanish outright, which read
  // as "the question moved/disappeared" and left shoppers unsure whether a
  // DST charge was still in play after they added a DTF spot. State is
  // recomputed on every placement/method change.
  Controller.prototype.updateDstVisibility = function () {
    var ui = dstUiState(this.state.placements, this.activePlacementId, this.state.dstStatus);
    var sec = this.els.dstSection;
    if (!sec) return;
    sec.hidden = false;
    sec.classList.toggle('is-disabled', ui.disabled);
    sec.classList.toggle('is-dst-saved', ui.savedWhileAddingNonDst);
    sec.setAttribute('aria-disabled', ui.disabled ? 'true' : 'false');
    this.els.dstOpts.forEach(function (btn) { btn.disabled = ui.disabled; });
    var na = sec.querySelector('[data-fp-dst-na]');
    if (na) na.hidden = ui.hasEmbLogo;
    var saved = sec.querySelector('[data-fp-dst-saved]');
    if (saved) saved.hidden = !ui.savedWhileAddingNonDst;
    // The answer only counts while the question applies; leaving a stale
    // "Nie" selected would keep charging DST for an order with no embroidered
    // logo left in it.
    if (!ui.hasEmbLogo) {
      this.state.dstStatus = null;
      this.els.dstOpts.forEach(function (b) { b.classList.remove('is-selected'); });
    }
    if (this.els.dstNote) {
      if (ui.disabled) {
        this.els.dstNote.hidden = true;
      } else if (this.state.dstStatus === 'no') {
        this.els.dstNote.hidden = false;
        this.els.dstNote.textContent = 'DST file preparation: +£20.00 once for this project.';
      } else if (this.state.dstStatus === 'unsure') {
        this.els.dstNote.hidden = false;
        this.els.dstNote.textContent = 'We reserve the DST preparation at £20.00. If you supply a valid file before production, the charge can be removed after contacting support.';
      } else {
        this.els.dstNote.hidden = true;
      }
    }
  };

  Controller.prototype.setPlacementMethod = function (id, method) {
    var chosen = this.state.placements.filter(function (c) { return c.id === id; })[0];
    if (!chosen || chosen.saved || this.activePlacementId !== id) return;
    var placement = byId(PLACEMENTS, id);
    if (!placement || placement.methods.indexOf(method) === -1) return;
    if (!this.methodAllowed(placement, method)) return;
    chosen.method = method;
    if (method !== 'embroidery') {
      chosen.embMode = null;
      chosen.text = '';
    } else if (!chosen.embMode) {
      chosen.embMode = 'logo'; // sensible default the first time a placement switches to Haft
    }
    this.renderChosenPlacements();
    this.renderSummary();
    this.updateConfirmState();
    this.clearFieldError('text');
  };

  Controller.prototype.setPlacementEmbMode = function (id, mode) {
    var chosen = this.state.placements.filter(function (c) { return c.id === id; })[0];
    if (!chosen || chosen.saved || this.activePlacementId !== id || chosen.method !== 'embroidery') return;
    chosen.embMode = mode;
    if (mode !== 'text') chosen.text = '';
    this.renderChosenPlacements();
    this.renderSummary();
    this.updateConfirmState();
    this.clearFieldError('text');
  };

  // Deliberately does NOT call renderChosenPlacements() -- that rebuilds
  // the whole chip row via innerHTML, which would destroy and recreate
  // this very input on every keystroke and throw away focus/cursor
  // position.
  Controller.prototype.setPlacementText = function (id, text) {
    var chosen = this.state.placements.filter(function (c) { return c.id === id; })[0];
    if (!chosen || chosen.saved || this.activePlacementId !== id) return;
    chosen.text = text.slice(0, 30);
    this.renderSummary();
    this.updateConfirmState();
    this.clearFieldError('text');
  };

  Controller.prototype.placementIsComplete = function (chosen) {
    return !!chosen && !(chosen.method === 'embroidery' && chosen.embMode === 'text' && !String(chosen.text || '').trim());
  };

  Controller.prototype.commitPlacement = function (id, addAnother) {
    var chosen = this.state.placements.filter(function (c) { return c.id === id; })[0];
    if (!chosen || chosen.saved || this.activePlacementId !== id) return false;
    if (!this.placementIsComplete(chosen)) {
      this._activeErrors = ['text'];
      if (this.els.footError) {
        this.els.footError.hidden = false;
        this.els.footError.textContent = 'Enter the text to embroider before saving this placement.';
      }
      var textInput = this.els.chosenWrap && this.els.chosenWrap.querySelector('[data-fp-chip-text][data-fp-chip-for="' + id + '"]');
      if (textInput && textInput.focus) textInput.focus();
      return false;
    }

    chosen.saved = true;
    this.activePlacementId = null;
    this.clearFieldError('text');
    this.refreshPlacementAvailability();
    this.renderChosenPlacements();
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();

    if (this.els.liveStatus) {
      this.els.liveStatus.textContent = addAnother
        ? 'Placement saved. Choose another placement.'
        : 'All selected placements are saved.';
    }
    if (addAnother) {
      var nextCard = this.els.placementCards.filter(function (card) { return !card.disabled; })[0];
      if (nextCard && nextCard.focus) nextCard.focus();
    } else {
      var hasEmbLogo = this.state.placements.some(function (c) { return c.method === 'embroidery' && c.embMode === 'logo'; });
      var nextControl = hasEmbLogo ? this.els.dstOpts[0] : this.els.confirmBtn;
      if (nextControl && nextControl.focus) nextControl.focus();
    }
    return true;
  };

  Controller.prototype.addAnotherPlacement = function () {
    if (this.activePlacementId && !this.commitPlacement(this.activePlacementId, true)) return;
    var nextCard = this.els.placementCards.filter(function (card) { return !card.disabled; })[0];
    if (nextCard && nextCard.focus) nextCard.focus();
  };

  Controller.prototype.editPlacement = function (id) {
    if (this.activePlacementId && this.activePlacementId !== id) return;
    var chosen = this.state.placements.filter(function (c) { return c.id === id; })[0];
    if (!chosen || !chosen.saved) return;
    chosen.saved = false;
    this.activePlacementId = id;
    this.refreshPlacementAvailability();
    this.renderChosenPlacements();
    this.updateConfirmState();
    var firstMethod = this.els.chosenWrap && this.els.chosenWrap.querySelector('[data-fp-chip="' + id + '"] [data-fp-chip-method]');
    if (firstMethod && firstMethod.focus) firstMethod.focus();
  };

  Controller.prototype.wireEvents = function () {
    var self = this;

    this.els.closeBtns.forEach(function (b) { b.addEventListener('click', function () { self.close(); }); });

    // Upload: click, keyboard, drag-and-drop
    if (this.els.fileBtn && this.els.fileInput) {
      this.els.fileBtn.addEventListener('click', function () { self.els.fileInput.click(); });
    }
    if (this.els.dropzone && this.els.fileInput) {
      this.els.dropzone.addEventListener('click', function () { self.els.fileInput.click(); });
      this.els.dropzone.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.els.fileInput.click(); }
      });
      ['dragenter', 'dragover'].forEach(function (evt) {
        self.els.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); self.els.dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        self.els.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); self.els.dropzone.classList.remove('is-dragover');
        });
      });
      this.els.dropzone.addEventListener('drop', function (e) {
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) self.handleFile(file);
      });
    }
    if (this.els.fileInput) {
      this.els.fileInput.addEventListener('change', function () {
        var file = self.els.fileInput.files && self.els.fileInput.files[0];
        if (file) self.handleFile(file);
        self.els.fileInput.value = '';
      });
    }
    if (this.els.artworkRemove) {
      this.els.artworkRemove.addEventListener('click', function () { self.clearArtwork(); });
    }
    if (this.els.artworkReplace) {
      this.els.artworkReplace.addEventListener('click', function () { self.els.fileInput.click(); });
    }

    // Rights checkbox
    if (this.els.rightsCheckbox) {
      this.els.rightsCheckbox.addEventListener('change', function () {
        self.state.rightsConfirmed = self.els.rightsCheckbox.checked;
        self.clearFieldError('rights');
        self.syncRightsGate();
        self.updateConfirmState();
      });
    }

    // Placement tiles only start a new editable row. Selected placements are
    // managed through their explicit Edit/Remove controls below.
    if (this.els.placementsWrap) {
      this.els.placementsWrap.addEventListener('click', function (e) {
        var card = e.target.closest('[data-fp-placement-card]');
        if (!card || card.disabled) return;
        var id = card.getAttribute('data-fp-placement-card');
        self.addPlacement(id);
      });
    }

    // Chosen-placement rows: click × to remove, click a method/embtype button to change that one
    // placement's DTF/Haft and Logo/Tekst choice, type in the text input
    // for Haft+Tekst placements.
    if (this.els.chosenWrap) {
      this.els.chosenWrap.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('[data-fp-chip-remove]');
        if (removeBtn) { self.removePlacement(removeBtn.getAttribute('data-fp-chip-remove')); return; }
        var editBtn = e.target.closest('[data-fp-chip-edit]');
        if (editBtn) { self.editPlacement(editBtn.getAttribute('data-fp-chip-edit')); return; }
        var methodBtn = e.target.closest('[data-fp-chip-method]');
        if (methodBtn) { self.setPlacementMethod(methodBtn.getAttribute('data-fp-chip-for'), methodBtn.getAttribute('data-fp-chip-method')); return; }
        var embtypeBtn = e.target.closest('[data-fp-chip-embtype]');
        if (embtypeBtn) { self.setPlacementEmbMode(embtypeBtn.getAttribute('data-fp-chip-for'), embtypeBtn.getAttribute('data-fp-chip-embtype')); return; }
      });
      this.els.chosenWrap.addEventListener('input', function (e) {
        var textInput = e.target.closest('[data-fp-chip-text]');
        if (textInput) self.setPlacementText(textInput.getAttribute('data-fp-chip-for'), textInput.value);
      });
    }

    // 3D-style rotation: a real slider (-180..180) drives a CSS
    // perspective/rotateY on the two garment faces (see ffc-personalisation
    // .css), so dragging genuinely turns the shirt rather than swapping
    // between two static "front/back" buttons.
    if (this.els.rotateSlider) {
      this.els.rotateSlider.addEventListener('input', function () {
        self.setRotation(Number(self.els.rotateSlider.value));
      });
    }
    this.els.rotateNudgeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.rotateTo(self.rotation + Number(btn.getAttribute('data-fp-rotate-nudge')));
      });
    });

    // DST question
    if (this.els.garmentSwatches) {
      this.els.garmentSwatches.addEventListener('click', function (ev) {
        var btn = ev.target.closest('[data-fp-swatch-colour]');
        if (!btn) return;
        self.setGarmentColour(btn.getAttribute('data-fp-swatch-colour'));
        self.markSelectedSwatch(self.els.garmentSwatches, btn);
      });
    }
    this.els.bgBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.setStageBackground(btn.getAttribute('data-fp-bg'));
        self.markSelectedSwatch(btn.parentNode, btn);
      });
    });

    this.els.dstOpts.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.state.dstStatus = btn.getAttribute('data-fp-dst');
        self.els.dstOpts.forEach(function (b) { b.classList.toggle('is-selected', b === btn); });
        self.updateDstVisibility();
        self.renderSummary();
        self.updateConfirmState();
        self.clearFieldError('dst');
      });
    });

    // Notes
    if (this.els.notes) {
      this.els.notes.addEventListener('input', function () {
        self.state.notes = self.els.notes.value.slice(0, 500);
      });
    }

    if (this.els.addAnotherBtn) {
      this.els.addAnotherBtn.addEventListener('click', function () { self.addAnotherPlacement(); });
    }

    // Confirm
    if (this.els.confirmBtn) {
      this.els.confirmBtn.addEventListener('click', function () { self.confirm(); });
    }

    // Esc to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.root.hidden) self.close();
    });
  };

  Controller.prototype.addPlacement = function (id) {
    var placement = byId(PLACEMENTS, id);
    if (!placement) return;
    if (this.activePlacementId) return;
    if (this.state.placements.some(function (c) { return c.id === id; })) return; // already added
    // Defaults to this placement's first allowed method (always 'dtf' for
    // every current PLACEMENTS entry) -- the shopper can switch it to Haft
    // per-placement afterwards via the chip's own method buttons.
    var self = this;
    var firstMethod = placement.methods.filter(function (m) {
      return self.methodAllowed(placement, m);
    })[0];
    if (!firstMethod) return;
    this.state.placements.push({ id: id, method: firstMethod, embMode: null, text: '', saved: false });
    this.activePlacementId = id;
    this.rotateTo(viewDegFor(placement)); // turn so the spot just added faces the shopper
    this.refreshPlacementAvailability();
    this.renderChosenPlacements();
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();
    this.clearFieldError('placement');
  };

  Controller.prototype.removePlacement = function (id) {
    this.state.placements = this.state.placements.filter(function (c) { return c.id !== id; });
    if (this.activePlacementId === id) this.activePlacementId = null;
    this.refreshPlacementAvailability();
    this.renderChosenPlacements();
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();
  };

  // Rotation now drives the real WebGL 3D model's Y-axis rotation (see
  // ffc-personalisation-3d.js `setRotation`) instead of a CSS rotateY on a
  // two-face SVG card -- the slider's -180..180 range and "front vs back"
  // framing stay the same from the shopper's point of view.
  Controller.prototype.setRotation = function (angle) {
    this.rotation = Math.max(-180, Math.min(180, angle));
    if (window.FFCPersonalisation3D) window.FFCPersonalisation3D.setRotation(this.rotation);
    if (this.els.rotateSlider) this.els.rotateSlider.value = this.rotation;
    if (this.els.liveStatus) {
      var abs = Math.abs(this.rotation);
      var facing = abs <= 45 ? 'front' : (abs >= 135 ? 'back' : (this.rotation > 0 ? 'left side' : 'right side'));
      this.els.liveStatus.textContent = 'Garment view: ' + facing + ' (' + Math.round(this.rotation) + '°).';
    }
  };

  // Animates to the nearest equivalent angle for "front" (always 0) or
  // "back" (+180 or -180, whichever is closer to the current angle) --
  // picking the wrong sign would spin the card almost all the way around
  // the long way instead of the short way.
  Controller.prototype.rotateTo = function (target) {
    if (target === 180 || target === -180) {
      target = this.rotation >= 0 ? 180 : -180;
    }
    this.setRotation(target);
  };

  Controller.prototype.applyGarmentColour = function () {
    if (window.FFCPersonalisation3D) window.FFCPersonalisation3D.setColour(this.garmentColour);
  };

  Controller.prototype.qualityChecker = function () {
    if (this._qualityChecker) return this._qualityChecker;
    var root = this.root.querySelector('[data-ffc-artwork-checker]');
    if (!root || !window.FFCArtworkChecker) return null;
    var self = this;
    this._qualityChecker = window.FFCArtworkChecker.attach(root, {
      onChange: function (quality) { self.state.quality = quality; self.updateConfirmState(); },
      onApply: function (file) { self.handleFile(file, true); }
    });
    return this._qualityChecker;
  };

  Controller.prototype.handleFile = function (file, edited) {
    var err = Upload.validate(file);
    if (this.els.uploadError) {
      this.els.uploadError.hidden = !err;
      this.els.uploadError.textContent = err || '';
    }
    if (err) { this.clearArtwork(); return; }
    var previousOriginalUrl = edited && this.state.artwork ? this.state.artwork.originalUrl || this.state.artwork.remoteUrl : '';
    if (edited && this.state.artwork && this.state.artwork.uploadEndpointConfigured && !previousOriginalUrl) return;

    if (this.artworkUrl) { URL.revokeObjectURL(this.artworkUrl); this.artworkUrl = null; }

    var canPreview = Upload.canPreviewVisually(file);
    var previewUrl = canPreview ? URL.createObjectURL(file) : null;
    this.artworkUrl = previewUrl;
    this.artworkAspect = null;

    var hasEndpoint = !!this.uploadEndpointUrl;
    this.state.artwork = {
      file: file,
      name: file.name,
      type: Upload.typeLabel(file),
      size: Upload.sizeLabel(file.size),
      previewUrl: previewUrl,
      canPreview: canPreview,
      // Server-side copy, so the owner actually receives the file instead
      // of just its filename (see ionos-upload-endpoint/). Blank endpoint
      // = feature not configured yet -- everything below just stays at
      // its default and behaves exactly like before this existed.
      uploadEndpointConfigured: hasEndpoint,
      uploadStatus: hasEndpoint ? 'pending' : 'none', // 'none' | 'pending' | 'done' | 'error'
      remoteUrl: null
    };
    this.state.artwork.originalUrl = previousOriginalUrl || '';
    var checker = this.qualityChecker();
    if (checker && !edited) checker.loadFile(file);
    else if (!checker) this.state.quality = null;

    this.renderArtworkCard();
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();
    this.clearFieldError('artwork');
    this.clearFieldError('uploadPending');
    this.clearFieldError('uploadFailed');

    if (canPreview && previewUrl) {
      var self = this;
      Upload.naturalAspect(previewUrl).then(function (aspect) {
        // Guard against a fast replace/remove happening before this resolves.
        if (self.state.artwork && self.state.artwork.previewUrl === previewUrl) {
          self.artworkAspect = aspect;
          self.renderPreview();
        }
      });
    }

    if (hasEndpoint) this.uploadArtwork(file, previewUrl);
  };

  // Sends the file to the configured endpoint (see ionos-upload-endpoint/
  // README.md for what that endpoint must do) and stores the returned
  // public URL. `previewUrl` is the identity check used throughout this
  // controller to detect "the shopper replaced/removed the artwork while
  // an earlier async operation was still in flight" -- same pattern as
  // the naturalAspect() guard in handleFile above.
  Controller.prototype.uploadArtwork = function (file, previewUrl) {
    var self = this;
    var uploadingArtwork = this.state.artwork;
    var form = new FormData();
    form.append('file', file);
    fetch(this.uploadEndpointUrl, { method: 'POST', body: form })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (result) {
        if (self.state.artwork !== uploadingArtwork) return;
        if (result.ok && result.body && result.body.ok && result.body.url) {
          self.state.artwork.uploadStatus = 'done';
          self.state.artwork.remoteUrl = result.body.url;
          if (!self.state.artwork.originalUrl) self.state.artwork.originalUrl = result.body.url;
        } else {
          self.state.artwork.uploadStatus = 'error';
          window.console && console.warn('FFC artwork upload failed', result.body);
        }
        self.renderArtworkCard();
        self.updateConfirmState();
      })
      .catch(function (uploadErr) {
        if (self.state.artwork !== uploadingArtwork) return;
        self.state.artwork.uploadStatus = 'error';
        window.console && console.warn('FFC artwork upload failed', uploadErr);
        self.renderArtworkCard();
        self.updateConfirmState();
      });
  };

  Controller.prototype.clearArtwork = function () {
    if (this.artworkUrl) { URL.revokeObjectURL(this.artworkUrl); this.artworkUrl = null; }
    this.state.artwork = null;
    this.state.quality = null;
    if (this._qualityChecker) this._qualityChecker.reset();
    this.artworkAspect = null;
    this.renderArtworkCard();
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();
  };

  Controller.prototype.renderArtworkCard = function () {
    var art = this.state.artwork;
    if (!this.els.artworkCard) return;
    if (!art) {
      this.els.artworkCard.hidden = true;
      if (this.els.dropzone) this.els.dropzone.hidden = false;
      return;
    }
    this.els.artworkCard.hidden = false;
    if (this.els.dropzone) this.els.dropzone.hidden = true;
    if (this.els.artworkThumb) {
      this.els.artworkThumb.innerHTML = art.canPreview && art.previewUrl
        ? '<img src="' + art.previewUrl + '" alt="">'
        : '<span aria-hidden="true">📄</span>';
    }
    if (this.els.artworkName) this.els.artworkName.textContent = art.name;
    if (this.els.artworkSub) this.els.artworkSub.textContent = art.type + ' · ' + art.size;
    if (this.els.artworkUploadStatus) {
      var statusEl = this.els.artworkUploadStatus;
      statusEl.classList.remove('is-pending', 'is-done', 'is-error');
      if (!art.uploadEndpointConfigured) {
        statusEl.hidden = true;
      } else if (art.uploadStatus === 'pending') {
        statusEl.hidden = false;
        statusEl.classList.add('is-pending');
        statusEl.textContent = 'Saving your logo…';
      } else if (art.uploadStatus === 'done') {
        statusEl.hidden = false;
        statusEl.classList.add('is-done');
        statusEl.textContent = '✓ Logo saved';
      } else if (art.uploadStatus === 'error') {
        statusEl.hidden = false;
        statusEl.classList.add('is-error');
        statusEl.textContent = '✕ Could not save the logo — try again';
      } else {
        statusEl.hidden = true;
      }
    }
  };

  // Preview: syncs `state.placements` to the real 3D model (see
  // ffc-personalisation-3d.js) instead of building 2D overlay divs -- every
  // chosen placement gets a decal on the model itself, which then just
  // rotates with it (no more separate front/back layers to keep in sync).
  // Falls back to a no-op if the 3D bridge isn't ready/available yet;
  // `open()` re-runs this once the model finishes loading so nothing is
  // silently dropped by a race between "shopper picked a placement" and
  // "GLB still downloading".
  Controller.prototype.renderPreview = function () {
    var self = this;
    var state = this.state;
    var canRenderLogo = !!(state.artwork && state.artwork.canPreview && state.artwork.previewUrl);
    var bridge = window.FFCPersonalisation3D;

    if (bridge && this._stage3dState === 'ready') {
      PLACEMENTS.forEach(function (def) {
        var chosen = state.placements.filter(function (c) { return c.id === def.id; })[0];
        if (chosen && canRenderLogo) {
          bridge.showDecal(def.id, state.artwork.previewUrl, self.artworkAspect || 1, 0, 0);
        } else {
          bridge.hideDecal(def.id);
        }
      });
    }

    if (this.els.stageEmpty) {
      var showEmpty = this._stage3dState === 'ready' && !canRenderLogo;
      this.els.stageEmpty.hidden = !showEmpty;
      this.els.stageEmpty.textContent = !state.artwork
        ? 'Upload a logo to see the preview on the garment.'
        : (state.artwork.canPreview ? '' : 'Preview is unavailable for PDF files — the logo will be applied to your chosen placement.');
    }

  };

  // Reflects the 3D bridge's load state onto the stage's status messages.
  // Personalisation (placements/pricing/artwork/confirm) works regardless
  // of whether the visual preview succeeds -- a failed/slow 3D load must
  // never block the shopper from finishing their order.
  Controller.prototype.setStage3dState = function (stateName) {
    this._stage3dState = stateName;
    if (this.els.stageLoading) this.els.stageLoading.hidden = stateName !== 'loading';
    if (this.els.stageError) this.els.stageError.hidden = stateName !== 'failed';
    this.renderPreview();
  };

  Controller.prototype.renderSummary = function () {
    var pricing = Pricing.compute(this.state, this.cfg, this.qty);
    if (this.els.summaryList) {
      this.els.summaryList.innerHTML = pricing.lines.map(function (l) {
        var value;
        if (l.included) value = '<dd class="is-included">Included</dd>';
        else if (l.flat != null) value = '<dd class="is-extra">+' + money(l.flat) + '</dd>';
        else if (l.perGarment) value = '<dd class="is-extra">+' + money(l.perGarment) + (l.perGarmentQty > 1 ? ' × ' + l.perGarmentQty + ' items' : '') + '</dd>';
        else value = '<dd>' + (l.note || '—') + '</dd>';
        return '<div class="ffc-personalisation__summary-row"><dt>' + l.label + '</dt>' + value + '</div>';
      }).join('');
    }
    if (this.els.summaryTotal) this.els.summaryTotal.textContent = pricing.total > 0 ? money(pricing.total) : 'Included';
    return pricing;
  };

  Controller.prototype.clearFieldError = function (key) {
    if (!this._activeErrors) return;
    var i = this._activeErrors.indexOf(key);
    if (i > -1) this._activeErrors.splice(i, 1);
    if (!this._activeErrors.length && this.els.footError) this.els.footError.hidden = true;
  };

  Controller.prototype.syncRightsGate = function () {
    var locked = !this.state.rightsConfirmed;
    this.els.rightsGated.forEach(function (node) {
      node.classList.toggle('is-rights-locked', locked);
      if (locked) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
  };

  // Keeps the Confirm button's disabled state in sync with validation on
  // every relevant change, rather than only checking on click -- the
  // button must read as inactive until rights are confirmed (and every
  // other required field is filled), not just reject a click afterwards.
  Controller.prototype.updateConfirmState = function () {
    if (!this.els.confirmBtn) return;
    var dtf = this.state.placements.filter(function (p) { return p.method === 'dtf'; });
    var compactQuality = this.root && this.root.querySelector('[data-aq-embedded]');
    if (compactQuality) compactQuality.hidden = dtf.length === 0;
    if (this._qualityChecker) {
      this._qualityChecker.setPresets(dtf.map(function (p) { return p.id === 'back' ? 'back' : p.id === 'neck' ? 'neck' : p.id.indexOf('sleeve') !== -1 ? 'sleeve' : 'chest'; }), dtf.length > 0);
      this.state.quality = this._qualityChecker.snapshot();
      var qc = this._qualityChecker;
      qc.uploadPending = !!(this.state.artwork && this.state.artwork.uploadEndpointConfigured && this.state.artwork.uploadStatus !== 'done');
      if(qc.els.apply) qc.els.apply.disabled = qc.busy || !!qc.enhancement || !qc.dirty || !qc.current || qc.current.width * qc.current.height > 10000000 || qc.uploadPending;
      if(qc.els['enhance-use']) qc.els['enhance-use'].disabled = qc.busy || !qc.enhancement || qc.uploadPending;
    }
    // Zabezpieczenie warstwowe: koszyk nie powinien w ogole otworzyc panelu
    // ponizej progu, ale jesli otworzy, zatwierdzenie ma byc niemozliwe.
    if (this.eligible === false) {
      this.els.confirmBtn.disabled = true;
      if (this.els.footError) {
        this.els.footError.hidden = false;
        this.els.footError.textContent =
          'Personalisation is available from ' + (this.cfg.minQty || DEFAULT_PRICING.minQty) + ' garments.';
      }
      return;
    }
    var missing = Validation.check(this.state);
    var pricing = Pricing.compute(this.state, this.cfg, this.qty);
    this.els.confirmBtn.disabled = missing.length > 0 || pricing.manualQuote || !this.methodsValid();
    if (missing.indexOf('artworkEditing') !== -1 && this.els.footError) {
      this.els.footError.hidden = false;
      this.els.footError.textContent = this._qualityChecker.t('finish_edits');
    } else if (missing.indexOf('artworkQuality') !== -1 && this.els.footError) {
      this.els.footError.hidden = false;
      this.els.footError.textContent = this._qualityChecker ? this._qualityChecker.t('missing_check') : 'Please reload to complete the DTF artwork quality check.';
    } else if (!missing.length && this.els.footError) this.els.footError.hidden = true;
    if (!missing.length && pricing.manualQuote && this.els.footError) {
      this.els.footError.hidden = false;
      this.els.footError.textContent = 'This configuration needs an individual quote. It will not be added to the cart with an unconfirmed price.';
    }
  };

  Controller.prototype.confirm = function () {
    if (this.eligible === false || !this.methodsValid()) {
      this.updateConfirmState();
      return;
    }
    if (this.activePlacementId && !this.commitPlacement(this.activePlacementId, false)) return;
    var missing = Validation.check(this.state);
    this._activeErrors = missing;
    if (missing.length) {
      if (this.els.footError) {
        this.els.footError.hidden = false;
        this.els.footError.textContent = missing[0] === 'artworkEditing' && this._qualityChecker ? this._qualityChecker.t('finish_edits') : missing[0] === 'artworkQuality' && this._qualityChecker ? this._qualityChecker.t('missing_check') : Validation.messageFor(missing[0]);
      }
      var focusMap = {
        placement: this.els.placementCards && this.els.placementCards[0],
        artwork: this.els.fileBtn,
        rights: this.els.rightsCheckbox,
        text: this.els.chosenWrap && this.els.chosenWrap.querySelector('[data-fp-chip-text]'),
        dst: this.els.dstOpts[0],
        uploadFailed: this.els.artworkReplace
      };
      var toFocus = focusMap[missing[0]];
      if (toFocus && toFocus.focus) toFocus.focus();
      return;
    }

    var pricing = this.renderSummary();
    if (pricing.manualQuote) {
      if (this.els.footError) {
        this.els.footError.hidden = false;
        this.els.footError.textContent = 'This configuration needs an individual quote.';
      }
      return;
    }
    var props = this.buildProperties(pricing);
    var structured = {
      placements: this.state.placements.map(function (placement) {
        return {
          id: placement.id,
          method: placement.method,
          embMode: placement.embMode || null,
          text: placement.text || ''
        };
      }),
      artwork: this.state.artwork ? {
        name: this.state.artwork.name,
        type: this.state.artwork.type,
        size: this.state.artwork.size,
        url: this.state.artwork.remoteUrl || '',
        quality: this.state.quality ? {
          version: this.state.quality.version,
          artworkSha256: this.state.quality.artworkSha256,
          processed: this.state.quality.processed,
          originalUrl: this.state.artwork.originalUrl || this.state.artwork.remoteUrl || '',
          acknowledgement: this.state.quality.acknowledgement,
          results: this.state.quality.results
        } : null
      } : null,
      rightsConfirmed: this.state.rightsConfirmed === true,
      dstStatus: this.state.dstStatus,
      notes: this.state.notes || ''
    };
    if (typeof this.onConfirm === 'function') this.onConfirm(props, pricing, structured);
    this.close();
  };

  Controller.prototype.buildProperties = function (pricing) {
    var state = this.state;
    var props = {};

    // One property PER placement (not one joined "A: x; B: y" string) --
    // Shopify's cart line item display renders each property as its own
    // row, so this is what actually keeps every placement readable on its
    // own line instead of wrapping together.
    state.placements.forEach(function (c, idx) {
      var p = byId(PLACEMENTS, c.id);
      var label = p ? p.label : c.id;
      var value;
      if (c.method === 'embroidery') {
        value = 'Embroidery — ' + (c.embMode === 'text' ? ('text ("' + c.text + '")') : 'logo');
      } else {
        value = 'DTF print';
      }
      props['Placement ' + (idx + 1) + ' — ' + label] = value;
    });

    var needsArtwork = state.placements.some(function (c) { return c.method === 'dtf' || (c.method === 'embroidery' && c.embMode === 'logo'); });
    if (needsArtwork && state.artwork) {
      props['Artwork'] = state.artwork.name + ' (' + state.artwork.type + ', ' + state.artwork.size + ')';
      // Only present when an upload endpoint is configured AND the upload
      // actually finished (Validation.check already blocks confirm while
      // uploadStatus is 'pending'/'error' when an endpoint is configured,
      // so reaching here with remoteUrl unset means no endpoint was set at
      // all -- the owner gets the filename above but not the file itself,
      // same as before this feature existed).
      if (state.artwork.remoteUrl) props['Artwork URL'] = state.artwork.remoteUrl;
      props['Artwork rights confirmed'] = state.rightsConfirmed ? 'Yes' : 'No';
    }

    var hasEmbLogo = state.placements.some(function (c) { return c.method === 'embroidery' && c.embMode === 'logo'; });
    if (hasEmbLogo) {
      props['Embroidered logo — DST file'] = state.dstStatus === 'yes' ? 'I have a ready DST file'
        : state.dstStatus === 'no' ? 'DST preparation (+£20 one-off)'
        : 'Not sure — DST preparation reserved (+£20)';
    }

    if (pricing && pricing.total > 0) {
      props['Personalisation charge'] = 'Calculated and verified at secure checkout';
      props['_fp_fee_total_pence'] = String(pricing.total);
    }
    props['_fp_pricing_model'] = 'per-placement-v2';
    props['_fp_dtf_placements'] = String(state.placements.filter(function (c) { return c.method === 'dtf'; }).length);
    props['_fp_embroidery_units'] = String(state.placements.filter(function (c) { return c.method === 'embroidery'; }).length);
    props['_fp_dst_setup'] = pricing && pricing.serviceQuantities && pricing.serviceQuantities.dst ? 'yes' : 'no';
    if (state.notes) props['Personalisation notes'] = state.notes;
    return props;
  };

  // Every open() starts a clean configuration -- artwork / placements /
  // method from a previously confirmed-and-closed session must never
  // silently carry over into the next one.
  Controller.prototype.resetForm = function () {
    if (this.artworkUrl) { URL.revokeObjectURL(this.artworkUrl); this.artworkUrl = null; }
    this.artworkAspect = null;
    this.state = this.freshState();
    if (this._qualityChecker) this._qualityChecker.reset();
    this.activePlacementId = null;
    this._activeErrors = [];

    if (this.els.dstSection) this.els.dstSection.hidden = true;

    if (this.els.fileInput) this.els.fileInput.value = '';
    this.renderArtworkCard();
    if (this.els.uploadError) this.els.uploadError.hidden = true;

    if (this.els.rightsCheckbox) this.els.rightsCheckbox.checked = false;
    this.syncRightsGate();

    this.refreshPlacementAvailability();
    this.renderChosenPlacements();

    this.els.dstOpts.forEach(function (b) { b.classList.remove('is-selected'); });
    if (this.els.dstNote) this.els.dstNote.hidden = true;

    if (this.els.notes) this.els.notes.value = '';
    this.setRotation(0);
    if (window.FFCPersonalisation3D) window.FFCPersonalisation3D.clearAllDecals();
  };

  Controller.prototype.open = function (opts) {
    opts = opts || {};
    this.qty = opts.qty || 1;
    this.onConfirm = opts.onConfirm || null;
    // Brak flagi = haft dozwolony (zachowanie sprzed wprowadzenia metafieldow).
    this.allowEmbroidery = opts.allowEmbroidery !== false;
    // Existing pack callers retain their explicitly configured flow. The cart
    // now supplies both flags from Liquid's product eligibility fields.
    this.allowDtf = opts.allowDtf !== false;
    this.methodUnavailable = opts.methodUnavailable || '';
    this.eligible = this.qty >= (this.cfg.minQty || DEFAULT_PRICING.minQty);
    this.cfg.includedDtfQty = Math.min(this.qty, Math.max(0, Number(opts.includedDtfQty) || 0));
    this.cfg.freeDtf = this.eligible && this.cfg.includedDtfQty > 0;
    if (opts.colourName) { this.setGarmentColour(opts.colourName); }
    this.colourOptions = Array.isArray(opts.colourOptions) ? opts.colourOptions.filter(Boolean) : [];
    this.renderGarmentSwatches();
    this.setStageBackground(this.stageBg || 'grey');

    this.resetForm();
    this.root.hidden = false;
    document.body.style.overflow = 'hidden';

    // The 3D model/three.js only start loading here, on first genuine open
    // -- never on plain page load. `activate()` is a no-op if already
    // loaded/loading; `onReady` fires immediately if it's already
    // ready/failed from a previous open, or later once the (single, lazy)
    // load settles.
    var self = this;
    var bridge = window.FFCPersonalisation3D;
    if (bridge) {
      this.setStage3dState('loading');
      bridge.activate();
      bridge.onReady(function (err) { self.setStage3dState(err ? 'failed' : 'ready'); });
    } else {
      this.setStage3dState('failed');
    }

    if (this.els.footError) this.els.footError.hidden = true;
    this.renderPreview();
    this.renderSummary();
    this.updateConfirmState();
    var heading = this.root.querySelector('[data-fp-heading]');
    if (heading) { heading.setAttribute('tabindex', '-1'); setTimeout(function () { heading.focus(); }, 0); }
  };

  Controller.prototype.close = function () {
    if (this.artworkUrl) { URL.revokeObjectURL(this.artworkUrl); this.artworkUrl = null; }
    this.root.hidden = true;
    document.body.style.overflow = '';
    if (window.FFCPersonalisation3D) window.FFCPersonalisation3D.deactivate();
  };

  Controller.prototype.setGarmentColour = function (name) {
    this.garmentColour = hexForColourName(name);
    this.garmentColourName = name || null;
    this.applyGarmentColour();
  };

  // Preview-only. The garment colour shown here never reaches the line-item
  // properties (garmentColour feeds the 3D bridge and nothing else), so
  // trying colours on is safe -- what ships is whatever variant is in the
  // cart.
  Controller.prototype.renderGarmentSwatches = function () {
    var wrap = this.els.garmentSwatches;
    if (!wrap) return;
    var real = (this.colourOptions || []).slice();
    var names = real.slice();
    var seenHex = {};
    names.forEach(function (n) { seenHex[hexForColourName(n)] = true; });
    // Skip a fallback whose colour a real cart colour already covers --
    // otherwise e.g. "Deep Black" and "Black" render as two identical
    // swatches, and both light up as selected.
    MAIN_PREVIEW_COLOURS.forEach(function (n) {
      var hex = hexForColourName(n);
      if (names.length < MIN_PREVIEW_SWATCHES && !seenHex[hex]) { seenHex[hex] = true; names.push(n); }
    });
    var currentName = this.garmentColourName;
    wrap.innerHTML = names.map(function (name, i) {
      var hex = hexForColourName(name);
      var isReal = i < real.length;
      return (
        '<button type="button" class="ffc-personalisation__swatch' +
          (name === currentName ? ' is-selected' : '') + (isReal ? ' is-real' : '') +
          '" data-fp-swatch-colour="' + escapeHtml(name) + '"' +
          ' style="--fp-swatch: ' + hex + '"' +
          ' title="' + escapeHtml(name) + (isReal ? ' — a colour from your cart' : '') + '">' +
          '<span class="visually-hidden">' + escapeHtml(name) + '</span>' +
        '</button>'
      );
    }, this).join('');
  };

  Controller.prototype.markSelectedSwatch = function (group, btn) {
    if (!group) return;
    Array.prototype.forEach.call(group.querySelectorAll('.ffc-personalisation__swatch'), function (b) {
      b.classList.toggle('is-selected', b === btn);
    });
  };

  // Grey by default: a white garment on the old near-white stage was
  // effectively invisible, which is the whole reason these controls exist.
  Controller.prototype.setStageBackground = function (key) {
    this.stageBg = key in STAGE_BACKGROUNDS ? key : 'grey';
    if (this.els.stage) this.els.stage.setAttribute('data-fp-bg-theme', this.stageBg);
  };

  // ---------------------------------------------------------------------
  // Boot + public bridge
  // ---------------------------------------------------------------------
  function boot() {
    var root = document.querySelector('[data-ffc-personalisation]');
    if (!root || root.dataset.fpReady === 'true') return;
    root.dataset.fpReady = 'true';

    var controller;
    try {
      controller = new Controller(root);
    } catch (e) {
      // Fail closed but never break the rest of the page (section 31).
      window.console && console.warn('FFC Personalisation failed to initialise:', e);
      return;
    }

    window.FFCPersonalisation = {
      open: function (opts) { controller.open(opts); },
      close: function () { controller.close(); },
      setGarmentColour: function (name) { controller.setGarmentColour(name); }
    };
  }

  // Small read-only bridge used by automated pricing tests and diagnostics.
  // The cart still receives its final quantities through onConfirm.
  window.FFCPersonalisationPricingV2 = {
    compute: function (state, cfg, qty) { return Pricing.compute(state, cfg, qty); },
    dstUiState: function (placements, activePlacementId, dstStatus) {
      return dstUiState(placements, activePlacementId, dstStatus);
    },
    defaults: DEFAULT_PRICING
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
